import {
  ListItemButton,
  Typography,
  Stack,
  Box,
  Tooltip,
  styled,
  SxProps,
  Theme,
} from '@mui/material'
import { Link } from 'react-router-dom'

const CustomizedButton = styled(ListItemButton)<{ $disablePadding?: boolean }>(
  ({ theme, $disablePadding }) => ({
    display: 'flex',
    flexDirection: 'row',
    padding: $disablePadding ? 0 : 8,
    textDecoration: 'none',
    borderRadius: 8,
    minWidth: 40,
    transition: theme.transitions.create('all', {
      duration: theme.transitions.duration.shortest,
    }),
    [theme.breakpoints.up('xs')]: {
      paddingLeft: $disablePadding ? 0 : 8,
      paddingRight: $disablePadding ? 0 : 8,
      gap: 8,
    },
    [theme.breakpoints.up('sm')]: {
      paddingLeft: $disablePadding ? 0 : 16,
      paddingRight: $disablePadding ? 0 : 16,
      gap: 16,
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: $disablePadding ? 0 : 8,
      paddingRight: $disablePadding ? 0 : 8,
      gap: 8,
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: $disablePadding ? 0 : 16,
      paddingRight: $disablePadding ? 0 : 16,
      gap: 16,
    },
    '&:hover': {
      background: theme.palette.action.hover,
    },
    '&:active': {
      background: theme.palette.action.focus,
    },
    '&:after': {
      content: '""',
      pointerEvents: 'none',
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: -8,
      backgroundColor: 'black',
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      width: 0,
      height: 4,
      transition: theme.transitions.create(['width', 'transform']),
    },
    '&.selected:after': {
      width: 'calc(100% - 16px)',
      marginRight: 8,
    },
  }),
)

const CustomizedTypography = styled(Typography)(({ theme }) => ({
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('xs')]: { display: 'none' },
  [theme.breakpoints.up('md')]: { display: 'initial' },
}))

type NavigationButtonProps = {
  alt?: string
  label?: string
  disablePadding?: boolean
  disabled?: boolean
  selected?: boolean
  icon?: string
  to?: string
  onClick?: () => void
  sx?: SxProps<Theme>
  StackProps?: {
    sx?: SxProps<Theme>
  }
}

export const NavigationButton = ({
  alt,
  label,
  icon,
  disablePadding,
  disabled,
  selected = false,
  to = '',
  onClick,
  sx,
  StackProps,
}: NavigationButtonProps) => {
  const Content = () => (
    <Stack alignItems="center" direction="row" gap={1} {...StackProps}>
      {icon && (
        <Box
          alt={alt ?? label}
          component="img"
          src={icon}
          sx={{ width: '100%' }}
        />
      )}
      {label && <CustomizedTypography>{label}</CustomizedTypography>}
    </Stack>
  )

  return (
    <Tooltip title={label ?? ''}>
      <CustomizedButton
        {...(!disabled && to && { to, component: Link })}
        className={disabled ? 'disabled' : selected ? 'selected' : undefined}
        data-testid="navigation-element"
        sx={{
          opacity: disabled ? 0.5 : 'initial',
          pointerEvents: disabled ? 'none' : 'initial',
          '& > *': { pointerEvents: disabled ? 'none' : 'initial' },
          mx: { xs: 0.25, sm: 0.5 },
          px: { xs: disablePadding ? 0 : 1, sm: disablePadding ? 0 : 2 },
          p: { xs: disablePadding ? 0 : undefined },
          width: 'fit-content',
          ...sx,
        }}
        tabIndex={disabled ? -1 : 0}
        onClick={onClick}
      >
        <Content />
      </CustomizedButton>
    </Tooltip>
  )
}

export default NavigationButton
